import {
  RouteLocationNormalizedGeneric,
  RouteRecordRaw,
  NavigationGuardNext,
} from 'vue-router';
import { useNewBookingStore } from 'src/stores/newBookingStore';
import { useAuthStore } from 'src/stores/authStore';
import { usePropertyStore } from 'src/stores/propertyStore';
import { usePreCheckInStore } from 'src/stores/preCheckInStore';
import { useManageBookingStore } from 'src/stores/manageBookingStore';
import { usePayByLinkStore } from 'src/stores/payByLinkStore';

const routes: RouteRecordRaw[] = [
  {
    path: '/Book/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const ps = usePropertyStore();
      // Something is not right, force the page to reload.
      if (ps.propertyId == undefined) {
        window.location.href = to.fullPath; // ← hard refresh + keeps query/hash
        next(false);
      } else {
        const nbStore = useNewBookingStore();
        nbStore.getBookConfig(ps.propertyId).finally(() => {
          next();
        });
      }
    },
    children: [
      {
        name: 'Book',
        path: '',
        component: () => import('pages/Book/SearchPage.vue'),
      },
      {
        name: 'Rooms-And-Rates',
        path: '/Book/:propertyCode/Rooms-And-Rates',
        component: () => import('pages/Book/RoomsAndRatesPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const newBookingStore = useNewBookingStore();
          if (!newBookingStore.canSearch) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'Checkout',
        path: '/Book/:propertyCode/Checkout/:confirmationToken?',
        component: () => import('pages/Book/CheckoutPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const newBookingStore = useNewBookingStore();
          const confirmationToken = to.params['confirmationToken'];
          if (!newBookingStore.hasReservationItems && !confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'Status',
        path: '/Book/:propertyCode/Status/:confirmationToken',
        component: () => import('pages/Book/StatusPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const confirmationToken = to.params['confirmationToken'];
          if (!confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'Confirmation',
        path: '/Book/:propertyCode/Confirmation/:confirmationToken',
        component: () => import('pages/Book/ConfirmationPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const confirmationToken = to.params['confirmationToken'];
          if (!confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
    ],
  },
  {
    path: '/Manage/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: async () => {
      const ps = usePropertyStore();
      const mbStore = useManageBookingStore();
      try {
        await mbStore.getManageConfig(ps.propertyId);
      } catch (e) {
        console.error(e);
      }
    },
    children: [
      {
        name: 'Manage',
        path: '',
        component: () => import('pages/Manage/SearchPage.vue'),
      },
      {
        name: 'Reservation',
        path: '/Manage/:propertyCode/Reservation/:referenceNumber/:code?',
        component: () => import('pages/Manage/ReservationPage.vue'),
        beforeEnter: (
          to: RouteLocationNormalizedGeneric,
          from: RouteLocationNormalizedGeneric,
          next: NavigationGuardNext,
        ) => {
          const returnUrl = {
            name: 'Manage',
            params: { propertyCode: to.params.propertyCode },
          };
          const mbStore = useManageBookingStore();

          const referenceNumber = to.params['referenceNumber'] as string;
          const code = to.params['code'] as string;
          if (referenceNumber && !code) {
            if (mbStore.canSearchBasedOnGuest && mbStore.reservation != null) {
              next();
            } else {
              mbStore.getCurrent().then(() => {
                next();
              }).catch(() => {
                next(returnUrl);
              })
            }
          } else {
            if (mbStore.reservation == null) {
              mbStore.reservationNumber = referenceNumber;
              mbStore
                .searchForReservation(code)
                .then(() => {
                  next();
                })
                .catch(() => {
                  next(returnUrl);
                });
            } else {
              next();
            }
          }
        },
      },
    ],
  },
  {
    path: '/PreCheckIn/:propertyCode/:referenceNumber/:code',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const ps = usePropertyStore();
      const pcStore = usePreCheckInStore();
      pcStore.getPreCheckInConfig(ps.propertyId).finally(() => {
        next();
      });
    },
    children: [
      {
        name: 'PreCheckIn',
        path: '',
        component: () => import('pages/PreCheckIn/PreCheckInPage.vue'),
      },
    ],
  },
  {
    path: '/BookGroup/:propertyCode',
    component: () => import('layouts/PropertyGroupLayout.vue'),
    children: [
      {
        name: 'BookGroup',
        path: '',
        component: () =>
          import('pages/PropertyGroup/PropertyGroupSearchPage.vue'),
      },
    ],
  },
  {
    path: '/SecurePay/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const ps = usePropertyStore();
      const pblStore = usePayByLinkStore();
      pblStore.getPayByLinkConfig(ps.propertyId).finally(() => {
        next();
      });
    },
    children: [
      {
        name: 'SecurePayError',
        path: '/SecurePay/:propertyCode/:confirmationToken',
        component: () => import('pages/PayByLink/PayByLinkPage.vue'),
        beforeEnter: (
          to: RouteLocationNormalizedGeneric,
          from: RouteLocationNormalizedGeneric,
          next: NavigationGuardNext,
        ) => {
          const confirmationToken = to.params['confirmationToken'] as string;
          const pblStore = usePayByLinkStore();
          const errorUrl = {
            name: 'PayByLinkError',
            params: to.params,
            query: to.query,
          };

          if (confirmationToken) {
            const hasSession =
              pblStore.hydrateStateFromSession(confirmationToken);
            if (hasSession) {
              pblStore.checkStatus(confirmationToken);
              next();
            } else {
              next(errorUrl);
            }
          } else {
            next(errorUrl);
          }
        },
      },
      {
        name: 'SecurePay',
        path: '/SecurePay/:propertyCode/:actionType/:invoiceNumber/:code',
        component: () => import('pages/PayByLink/PayByLinkPage.vue'),
        beforeEnter: (
          to: RouteLocationNormalizedGeneric,
          from: RouteLocationNormalizedGeneric,
          next: NavigationGuardNext,
        ) => {
          const returnUrl = {
            name: 'PayByLinkError',
            params: to.params,
            query: to.query,
          };
          const pblStore = usePayByLinkStore();

          const actionType = to.params['actionType'] as string;
          const invoiceNumber = to.params['invoiceNumber'] as string;
          const code = to.params['code'] as string;
          const id = to.query['id'] as string;
          if (!actionType && !invoiceNumber && !code) {
            next(returnUrl);
          } else {
            pblStore
              .getPayByLinkInformation(actionType, invoiceNumber, code, id)
              .then(() => {
                next();
              })
              .catch(() => {
                next(returnUrl);
              });
          }
        },
      },
      {
        name: 'PayByLinkStatus',
        path: '/SecurePay/:propertyCode/Status/:confirmationToken',
        component: () => import('pages/PayByLink/PayByLinkStatusPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const confirmationToken = to.params['confirmationToken'];
          if (!confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'PayByLinkConfirmation',
        path: '/SecurePay/:propertyCode/Confirmation/:confirmationToken',
        component: () =>
          import('pages/PayByLink/PayByLinkConfirmationPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const confirmationToken = to.params['confirmationToken'];
          if (!confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'PayByLinkError',
        path: '/SecurePay/:propertyCode/:actionType?/:invoiceNumber?/:code?',
        component: () => import('pages/PayByLink/PayByLinkErrorPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ForgotPassword/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ForgotPassword',
        path: '',
        component: () => import('pages/Account/ForgotPasswordPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ResetPassword/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ResetPassword',
        path: '',
        component: () => import('pages/Account/ResetPasswordPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ConfirmEmail/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ConfirmEmail',
        path: '',
        component: () => import('pages/Account/ConfirmEmailPage.vue'),
      },
    ],
  },
  {
    path: '/Account/Register/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'Register',
        path: '',
        component: () => import('pages/Account/RegisterPage.vue'),
      },
    ],
  },
  {
    path: '/ManageAccount/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: async (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const authStore = useAuthStore();
      const ps = usePropertyStore();
      await authStore.getUser(ps.propertyId);

      const currentRoute = authStore.router.currentRoute;
      if (authStore.isUserLoggedIn) {
        next();
      } else {
        // redirect them to book
        next({
          name: 'Book',
          params: { propertyCode: to.params.propertyCode },
          query: currentRoute.value.query,
          hash: currentRoute.value.hash,
        });
      }
    },
    children: [
      {
        name: 'ManageAccount',
        path: '',
        component: () => import('pages/Account/ManageAccountPage.vue'),
      },
    ],
  },
  {
    path: '/Unauthorised',
    children: [
      {
        name: 'Unauthorised',
        path: '',
        component: () => import('pages/UnauthorisedPage.vue'),
      },
    ],
  },
  {
    path: '/Error',
    children: [
      {
        name: 'Error',
        path: '',
        component: () => import('pages/ErrorNotFound.vue'),
      },
    ],
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
];

export default routes;
